import { useState, type FormEvent } from 'react';
import type { MeDto } from '@setu/shared';
import { api, apiErrorText } from '../api.ts';
import { ForgotPasswordForm } from './PasswordHelp.tsx';

/**
 * Sign in. There is no "create an account" here: Setu is invitation only, and
 * the link in the invitation email is where an account begins.
 */
export default function SignIn({ onSignedIn }: { onSignedIn: (user: MeDto) => void }): React.ReactElement {
  const [forgot, setForgot] = useState(false);
  return (
    <div className="gate">
      <div className="card stack">
        <div>
          <div className="brand" style={{ paddingLeft: 0 }}>
            <b>Setu</b>
            <span>the bridge</span>
          </div>
          <h1 style={{ marginTop: 14 }}>{forgot ? 'Forgot your password?' : 'Welcome back.'}</h1>
          <p className="soft" style={{ marginTop: 6 }}>
            One conversation at a time.
          </p>
        </div>

        {forgot ? (
          <ForgotPasswordForm onBack={() => setForgot(false)} />
        ) : (
          <SignInForm onSignedIn={onSignedIn} onForgot={() => setForgot(true)} />
        )}

        <p className="small muted">
          Setu is invitation only — if you don't have an account yet, your city coordinator can send you one.
        </p>
      </div>
    </div>
  );
}

function SignInForm({
  onSignedIn,
  onForgot,
}: {
  onSignedIn: (user: MeDto) => void;
  onForgot: () => void;
}): React.ReactElement {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user } = await api.login(email, password);
      onSignedIn(user);
    } catch (err) {
      setError(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="stack" onSubmit={submit}>
      <div className="field">
        <label htmlFor="email">Email</label>
        <input
          id="email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="password">Password</label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>

      {error && (
        <p className="notice alert" role="alert">
          {error}
        </p>
      )}

      <button className="btn block" type="submit" disabled={busy}>
        {busy ? 'One moment…' : 'Sign in'}
      </button>
      <div>
        <button type="button" className="linklike" onClick={onForgot}>
          Forgot your password?
        </button>
      </div>
    </form>
  );
}
